import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';
import { UserApi } from '@/api';
import { formatDateTime } from '@/lib/format';
import { Plus, Edit, Shield, Eye, EyeOff, Trash2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  marketing_manager: 'Marketing Manager',
};

export default function Users() {
  const { push } = useToast();
  const { user: me } = useAuth();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'manager' as 'admin' | 'manager' | 'marketing_manager',
    password: '',
    phone: '',
    whatsapp: '',
    active: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => UserApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => UserApi.create(data),
    onSuccess: () => {
      push({ tone: 'success', title: 'User created' });
      setModalOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to create user', body: err.message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => UserApi.update(editId!, data),
    onSuccess: () => {
      push({ tone: 'success', title: 'User updated' });
      setModalOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to update user', body: err.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => UserApi.remove(id),
    onSuccess: (data: any) => {
      push({ tone: 'success', title: data.deleted ? 'User deleted' : 'User deactivated' });
      setDeleteId(null);
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: any) => push({ tone: 'error', title: 'Failed to delete user', body: err.message }),
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      role: 'manager',
      password: '',
      phone: '',
      whatsapp: '',
      active: true,
    });
    setEditId(null);
    setErrors({});
    setShowPassword(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!editId && !formData.password) newErrors.password = 'Password is required for new users';
    if (formData.password && formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenModal = (user?: any) => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        password: '',
        phone: user.phone ?? '',
        whatsapp: user.whatsapp ?? '',
        active: user.active !== false,
      });
      setEditId(user.id);
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const data: any = {
      name: formData.name,
      email: formData.email,
      role: formData.role,
      active: formData.active,
      phone: formData.phone || null,
      whatsapp: formData.whatsapp || null,
    };

    if (formData.password) {
      data.password = formData.password;
    }

    if (editId) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const users = usersQuery.data || [];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Users"
        subtitle="Manage system users and roles"
        actions={
          <button
            className="btn btn-primary gap-2"
            onClick={() => handleOpenModal()}
          >
            <Plus size={16} /> Add User
          </button>
        }
      />

      {/* Table */}
      {usersQuery.isLoading ? (
        <div className="text-center py-12 text-muted">Loading…</div>
      ) : users.length === 0 ? (
        <EmptyState
          title="No users"
          body="Add your first user to get started."
          action={
            <button
              className="btn btn-primary"
              onClick={() => handleOpenModal()}
            >
              Add User
            </button>
          }
        />
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table-clean">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>2FA</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u: any) => (
                  <tr key={u.id}>
                    <td className="font-medium">{u.name}</td>
                    <td className="text-sm">{u.email}</td>
                    <td className="text-sm">
                      <div className="flex items-center gap-1">
                        {u.role === 'admin' && <Shield size={14} className="text-rdx" />}
                        <span>{ROLE_LABELS[u.role] ?? u.role}</span>
                      </div>
                    </td>
                    <td className="text-sm">
                      <span className={`badge ${u.active ? 'status-approved' : 'status-lost'}`}>
                        {u.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-sm">
                      {u.totp_enabled ? (
                        <span className="badge status-approved">Enabled</span>
                      ) : (
                        <span className="badge status-draft">Disabled</span>
                      )}
                    </td>
                    <td className="text-sm text-muted">
                      {u.active === false ? '-' : (u.last_login_at ? formatDateTime(u.last_login_at) : '-')}
                    </td>
                    <td className="flex gap-1">
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => handleOpenModal(u)}
                      >
                        <Edit size={14} />
                      </button>
                      {u.id !== me?.id && (
                        <button
                          className="btn btn-sm btn-ghost text-rdx hover:bg-red-50"
                          onClick={() => setDeleteId(u.id)}
                          title="Delete user"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card card-pad w-[360px] space-y-4">
            <h3 className="text-sm font-semibold text-ink">Delete user?</h3>
            <p className="text-xs text-muted">
              If this user has created PIs they will be deactivated instead of deleted.
            </p>
            <div className="flex gap-2 justify-end">
              <button className="btn btn-sm btn-secondary" onClick={() => setDeleteId(null)}>Cancel</button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => deleteMutation.mutate(deleteId)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          resetForm();
        }}
        title={editId ? 'Edit User' : 'Add User'}
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Save
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Name *</label>
            <input
              type="text"
              className={`input mt-1 ${errors.name ? 'input-error' : ''}`}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            {errors.name && <div className="text-xs text-rdx mt-1">{errors.name}</div>}
          </div>

          <div>
            <label className="label">Email *</label>
            <input
              type="email"
              className={`input mt-1 ${errors.email ? 'input-error' : ''}`}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={!!editId}
            />
            {errors.email && <div className="text-xs text-rdx mt-1">{errors.email}</div>}
          </div>

          <div>
            <label className="label">Role</label>
            <select
              className="input mt-1"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
            >
              <option value="manager">Manager</option>
              <option value="marketing_manager">Marketing Manager</option>
              <option value="admin">Admin</option>
            </select>
            <p className="text-xs text-muted mt-1">
              {formData.role === 'admin'
                ? 'Admins can approve PIs and manage system settings'
                : 'Managers can create and submit PIs for admin approval'}
            </p>
          </div>

          <div>
            <label className="label">{editId ? 'New Password (optional)' : 'Password *'}</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                className={`input mt-1 ${errors.password ? 'input-error' : ''}`}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={editId ? 'Leave blank to keep current password' : 'Min 8 characters'}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-ink"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && <div className="text-xs text-rdx mt-1">{errors.password}</div>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Phone</label>
              <input
                className="input mt-1"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+91 …"
              />
            </div>
            <div>
              <label className="label">WhatsApp</label>
              <input
                className="input mt-1"
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                placeholder="+91 …"
              />
              <p className="text-xs text-muted mt-1">
                Used to copy the sales person on PIs sent via "Approve &amp; Send".
              </p>
            </div>
          </div>

          {editId && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
              <span className="text-sm">Active</span>
            </label>
          )}
        </div>
      </Modal>
    </div>
  );
}
